import { FC } from 'react';
import cn from 'classnames';
import styled from 'styled-components';

import { NotificationListItem, useNotifications } from '@/entities/notification';
import { COLORS } from '@/shared/constants/colors';
import { Icon } from '@/shared/uikit/Icon';
import { Title } from '@/shared/uikit/Title';
import { dates } from '@/shared/utils/dates';

import { RichText } from './RichText';
import { Dot } from './styled';

type NotificationItemProps = {
  item: NotificationListItem;
  onMarkRead?: (id: string) => void;
};

export const NotificationItem: FC<NotificationItemProps> = ({ item, onMarkRead }) => {
  const { updateRead } = useNotifications();

  const handleClick = () => {
    updateRead(item.id);
    onMarkRead?.(item.id);
  };

  return (
    <Container className={cn({ unread: !item.isRead })}>
      <IconWrapper>
        {!item.isRead && <Dot className="info" />}
        <Icon name="info" size="s24" />
      </IconWrapper>
      <ContentWrapper>
        <div>
          <StyledTitle size={14} bold>
            {item.title}
          </StyledTitle>
          <RichText input={item.message} />
        </div>
        <DateWrapper>
          <p>{dates.formatDate2(item.date)}</p>
          <span>•</span>
          <p>{dates.formatTime(item.date)}</p>
        </DateWrapper>
      </ContentWrapper>
      {!item.isRead && <UnreadButton onClick={handleClick} />}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  justify-content: start;
  gap: 12px;
  padding: 16px 24px;
  position: relative;
  transition: background var(--ani-speed) ease-in-out;

  & + & {
    border-top: 1px solid ${COLORS.systemDark100};
  }

  &.unread:hover {
    background: ${COLORS.systemLight200};
  }
`;

const IconWrapper = styled.div`
  border-radius: 8px;
  background-color: ${COLORS.systemLight300};
  color: ${COLORS.systemDark200};
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  position: relative;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  line-height: 20px;
`;

const StyledTitle = styled(Title)`
  &&& {
    line-height: 20px;
  }
`;

const DateWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${COLORS.systemDark200};
  font-size: 12px;
  line-height: 16px;
`;

const UnreadButton = styled.button`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background: transparent;
  border: none;
  cursor: pointer;
`;
